import {
  Body,
  Box,
  Flex,
  SlideFade,
  system,
  transition,
} from '@hover/blueprint';
import React, { useContext, useState } from 'react';

import { ColorCatalogPicker } from 'src/modules/designStudio/components/DesignMenu/Filters/ColorCatalogPicker';
import { DebugDoorGroup } from 'src/modules/designStudio/components/DesignMenu/Menu/DoorGroupMenu/DebugHelpers';
import { DOOR_ENUM_TO_NAME } from 'src/modules/designStudio/components/DesignMenu/Menu/DoorGroupMenu/index';
import { MenuHeader } from 'src/modules/designStudio/components/DesignMenu/Menu/MenuHeader';
import { selectionStyles } from 'src/modules/designStudio/components/SelectionButton';
import type {
  DoorGroup,
  DoorTexture,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { useBuildingDesigner } from 'src/modules/designStudio/helpers/machete/useBuildingDesigner';
import {
  historyContext,
  productCatalogContext,
} from 'src/modules/designStudio/providers';
import { context } from 'src/modules/designStudio/providers/DesignStudioServiceProvider';
import type { MutateBuildingFunction } from 'src/modules/designStudio/providers/MacheteBuildingProvider/types';
import {
  WoodGroup,
  createColorCatalogForStyle,
  getNonWoodVariations,
  getWoodVariations,
} from 'src/modules/designStudio/providers/ProductCatalogProvider/doors';
import type {
  DoorStyle,
  DoorVariation,
} from 'src/modules/designStudio/providers/ProductCatalogProvider/doors/types';
import { CustomIcon } from 'src/shared/components';
import { FeatureFlag } from 'src/shared/constants';
import {
  TranslationNamespace,
  useTranslate,
  useTreatment,
} from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';
import type { ButtonPressed } from 'src/shared/telemetry';

export const StyleButton = ({
  styleName,
  onClick,
  icon,
  isSelected,
}: {
  styleName: string;
  onClick: () => void;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  isSelected: boolean;
}) => (
  <Flex
    alignContent="center"
    alignItems="center"
    direction="column"
    flexBasis={0}
    flexGrow={0}
    flexShrink={0}
    justifyContent="start"
  >
    <system.button
      {...selectionStyles({ isSelected })}
      borderRadius="50%"
      cursor="pointer"
      onClick={onClick}
      p={400}
      position="relative"
    >
      <Box borderRadius="50%" mb={0} px="0" py="0">
        <CustomIcon
          color="neutral.900"
          icon={icon}
          iconHeight="32px"
          iconWidth="32px"
          padding={0}
          {...transition.easing250('color')}
        />
      </Box>
    </system.button>
    <Body
      fontSize={200}
      fontWeight={400}
      lineHeight={200}
      my={200}
      p={0}
      textAlign="center"
      textColor="neutral.600"
    >
      {styleName}
    </Body>
  </Flex>
);

export const DOOR_TEXTURE_TOLERANCES: Record<DoorGroup, number> = {
  FRONT: 0.05,
  GARAGE: 0.25,
  SERVICE: 0.05,
  SLIDING_GLASS: Infinity,
};

export const updateDoorTextures = (
  mutateBuilding: (update: MutateBuildingFunction) => void,
  group: DoorGroup,
  style: DoorStyle,
  variationColor: string,
  isWood: boolean,
) => {
  /**
   * When the texture being applied is wood we don't supply color. Painting over a wooden texture will result in an overly dark appearance.
   */
  const color = isWood ? undefined : variationColor;

  const variations: DoorVariation[] = isWood
    ? getWoodVariations(style, variationColor)
    : getNonWoodVariations(style);

  const doorTextures: DoorTexture[] = variations.map(variation => ({
    textureId: variation.textureId,
    url: variation.url,
    width: variation.width,
    height: variation.height,
    canTile: variation.canTile,
  }));

  mutateBuilding(async mutations => {
    await mutations.changeDoors(
      group,
      doorTextures,
      color,
      DOOR_TEXTURE_TOLERANCES[group],
    );
  });
};

export const DoorGroupDetail = ({
  doorType,
  onBack,
  onClose,
}: {
  doorType: DoorGroup;
  onBack: () => void;
  onClose: () => void;
}) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const { doorCatalog } = useContext(productCatalogContext);
  const { mutateBuilding } = useBuildingDesigner();

  const [selectedStyleIndex, setSelectedStyleIndex] = useState<
    undefined | number
  >(undefined);
  const useDebugUI =
    useTreatment(FeatureFlag.DesignStudio.DS_DOOR_GROUPS_DEBUGGING) === 'on';

  const doorStyles = doorCatalog.stylesByDoorType[doorType].styles;

  const colorCatalogWithWood =
    selectedStyleIndex !== undefined
      ? createColorCatalogForStyle(
          doorStyles[selectedStyleIndex],
          doorCatalog.colors,
        )
      : doorCatalog.colors;

  const fallBackDefaultColorGroup = 'Popular';
  const defaultColorGroup = 'Woods';
  const defaultColorIndex = 0;

  const [selectedColorGroup, setSelectedColorGroup] =
    useState<string>(defaultColorGroup);

  const [selectedColorIndex, setSelectedColorIndex] =
    useState<number>(defaultColorIndex);

  const showColorUI = selectedStyleIndex !== undefined;

  const { historyState } = useContext(historyContext);
  const { appService } = useContext(context);
  const appSnapshot = appService.getSnapshot();

  const commonTrackingData = {
    primary_cta: false,
    job_id: historyState.jobId ?? undefined,
    org_id: appSnapshot.context.orgId,
    partner_id: appSnapshot.context.partnerId,
    user_id: appSnapshot.context.userId,
    user_email: historyState.email ?? undefined,
    page_or_screen_name: 'Design-studio',
    design_studio_mode: 'default',
    feature: 'doors',
    button_location: 'design-template-panel',
  } as ButtonPressed;

  const actions = {
    onStyleSelected: (index: number) => {
      const colorCatalogFromSelection = createColorCatalogForStyle(
        doorStyles[index],
        doorCatalog.colors,
      );
      const styleHasWood = !!colorCatalogFromSelection[defaultColorGroup];
      const colorGroup = styleHasWood
        ? defaultColorGroup
        : fallBackDefaultColorGroup;

      setSelectedStyleIndex(index);
      setSelectedColorGroup(colorGroup);
      setSelectedColorIndex(defaultColorIndex);
      const { hex } = colorCatalogFromSelection[colorGroup][defaultColorIndex];
      updateDoorTextures(
        mutateBuilding,
        doorType,
        doorStyles[index],
        hex,
        styleHasWood,
      );
      buttonPressed({
        ...commonTrackingData,
        track_id: 'door-product-selected',
        button_text: `${doorStyles[index].name}`,
      });
    },
    onColorCategorySelected: (colorGroup: string) => {
      setSelectedColorGroup(colorGroup);
      setSelectedColorIndex(defaultColorIndex);
      updateDoorTextures(
        mutateBuilding,
        doorType,
        doorStyles[selectedStyleIndex!],
        colorCatalogWithWood[colorGroup][defaultColorIndex].hex,
        colorGroup === WoodGroup,
      );
      buttonPressed({
        ...commonTrackingData,
        track_id: 'door-color-group-selection-button',
        button_text: `${colorGroup}`,
      });
    },
    onColorSelected: (index: number) => {
      setSelectedColorIndex(index);
      updateDoorTextures(
        mutateBuilding,
        doorType,
        doorStyles[selectedStyleIndex!],
        colorCatalogWithWood[selectedColorGroup][index].hex,
        selectedColorGroup === WoodGroup,
      );
      buttonPressed({
        ...commonTrackingData,
        track_id: 'door-color-selection-button',
        button_text: `${colorCatalogWithWood[selectedColorGroup][index].hex}`,
      });
    },
  };

  return (
    <Flex direction="column">
      <MenuHeader
        closeMenu={onClose}
        headerText={t('menu.doorGroups.title')}
        onBack={onBack}
      />
      <Body fontSize={300} fontWeight={700} lineHeight={500} m={0}>{`${
        DOOR_ENUM_TO_NAME[doorType]
      } ${t('menu.doorGroups.doorStyle')}`}</Body>
      <Flex
        direction="row"
        gap={300}
        my={100}
        overflowX="auto"
        padding={200}
        width="100%"
      >
        {doorStyles.map((style, index) => (
          <StyleButton
            icon={style.icon}
            isSelected={index === selectedStyleIndex}
            key={`door-style-button-${style.name}`}
            onClick={() => {
              actions.onStyleSelected(index);
            }}
            styleName={style.name}
          />
        ))}
      </Flex>
      <SlideFade in={showColorUI} offsetY="100%" unmountOnExit>
        <ColorCatalogPicker
          colorCatalog={colorCatalogWithWood}
          onColorSelected={actions.onColorSelected}
          onGroupSelected={actions.onColorCategorySelected}
          selectedColorIndex={selectedColorIndex}
          selectedGroup={selectedColorGroup}
        />
      </SlideFade>
      {useDebugUI && (
        <DebugDoorGroup catalog={doorCatalog} doorType={doorType} />
      )}
    </Flex>
  );
};
